import { ExclusiveChoiceDropdown } from "dash-styled"
import { turbine } from "neql"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNeqlContextProvider } from "../../../../../../../../../Hooks/useNeqlContextProvider"
import {
    StructuredSequence,
    useStructuredTrainingQuestionTutorial
} from "../../../../../../../../../Hooks/useStructuredTrainingQuestionTutorial"
import { APIJobQueryModel } from "../../../../../../../../../Models/API/JobModel"
import { FilterAndSortExpression } from "../../../../../../../../../Models/Client/FilterAndSortExpression"
import { JobContentPoolParams } from "./JobContentPool"
import { TutorialContentEmpty } from "./TutorialContentEmpty"
import { TutorialContentSequence } from "./TutorialContentSequence"
import { TutorialExampleModal } from "./TutorialExampleModal"
import { TutorialExplanationModal } from "./TutorialExplanationModal"

enum ContentOption {
    explanation = "Explanation",
    example = "Example"
}

type ContentOptionDropdown = string | ContentOption

// Array of strings for the content option labels
const contentOptions = Object.values(ContentOption).slice(
    0,
    Object.values(ContentOption).length / 2 + 1
)
interface TutorialContentProps {
    orgUsername: string
    appID: string
    qualifierID: string
    trainingId: string
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    params: JobContentPoolParams
}

export const TutorialContent = ({
    orgUsername,
    appID,
    qualifierID,
    trainingId,
    filterAndSort,
    params
}: TutorialContentProps) => {
    const clientContext = useNeqlContextProvider("turbine")
    const [showModal, setShowModal] = useState<ContentOptionDropdown>("")

    const [structuredSequence, sequenceRefetch] =
        useStructuredTrainingQuestionTutorial(trainingId)

    const [sequence, setSequence] =
        useState<StructuredSequence[] | undefined>(structuredSequence)

    const [questionToEdit, setQuestionToEdit] =
        useState<StructuredSequence | undefined>()

    const [createQuestionTutorial, { loading: creatingQuestionTutorial }] =
        turbine.useCreateQuestionTutorialMutation({ client: clientContext })

    const [updateQuestionTutorial, { loading: updatingQuestionTutorial }] =
        turbine.useUpdateQuestionTutorialMutation({
            client: clientContext
        })

    const [deleteQuestionTutorial] = turbine.useDeleteQuestionTutorialMutation({
        client: clientContext
    })

    const probableNextSequence = useMemo(
        () =>
            (structuredSequence?.[structuredSequence.length - 1]?.step ?? 0) +
            1,
        [structuredSequence]
    )
    const handleModalClose = useCallback(() => {
        setShowModal("")
        setQuestionToEdit(undefined)
    }, [])

    const handleTutorialQuestionCreation = useCallback<
        (question: {
            hint?: string
            explanation?: string
            jobId?: string
            baselineId?: string
            isInteractive?: boolean
        }) => Promise<void>
    >(
        async ({
            hint,
            explanation,
            jobId,
            baselineId,
            isInteractive = false
        }) => {
            if (!hint && !explanation && !jobId && !baselineId) {
                return
            }

            await createQuestionTutorial({
                variables: {
                    createQuestionTutorialTrainingId: trainingId,
                    createQuestionTutorialInteractive: isInteractive,
                    createQuestionTutorialType: jobId
                        ? turbine.TrainingQuestionTutorialType.FromJob
                        : baselineId
                        ? turbine.TrainingQuestionTutorialType.FromBaseline
                        : turbine.TrainingQuestionTutorialType.FreeMode,
                    createQuestionTutorialBaselineId: baselineId,
                    createQuestionTutorialJobId: jobId,
                    createQuestionTutorialHint: hint,
                    createQuestionTutorialExplanation: explanation,
                    createQuestionTutorialSequence: probableNextSequence
                }
            })
            sequenceRefetch()
        },
        [
            createQuestionTutorial,
            probableNextSequence,
            sequenceRefetch,
            trainingId
        ]
    )

    const handleMoveItem = useCallback(
        (dragIndex: number, hoverIndex: number) => {
            const sequenceCopy = [...(sequence ?? [])]
            const newSequence = [
                ...sequenceCopy.slice(0, dragIndex),
                ...sequenceCopy.slice(dragIndex + 1)
            ]
            newSequence.splice(hoverIndex, 0, sequenceCopy[dragIndex])
            newSequence.map((item, index) => (item.step = index + 1))
            setSequence(newSequence)
        },
        [sequence]
    )

    const handleEndMove = useCallback(
        async (id: string) => {
            const item = sequence?.find(item => item.id === id)

            await updateQuestionTutorial({
                variables: {
                    updateQuestionTutorialId: id,
                    updateQuestionTutorialSequence: item?.step
                }
            })
            await sequenceRefetch()
        },
        [sequence, sequenceRefetch, updateQuestionTutorial]
    )

    const handleEditQuestion = useCallback(
        async (
            id: string,
            hint?: string,
            explanation?: string,
            isInteractive?: boolean,
            jobId?: string,
            baselineId?: string
        ) => {
            await updateQuestionTutorial({
                variables: {
                    updateQuestionTutorialId: id,
                    updateQuestionTutorialType: jobId
                        ? turbine.TrainingQuestionTutorialType.FromJob
                        : baselineId
                        ? turbine.TrainingQuestionTutorialType.FromBaseline
                        : turbine.TrainingQuestionTutorialType.FreeMode,
                    updateQuestionTutorialHint: hint,
                    updateQuestionTutorialExplanation: explanation,
                    updateQuestionTutorialJobId: jobId,
                    updateQuestionTutorialBaselineId: baselineId,
                    updateQuestionTutorialInteractive: isInteractive
                }
            })
            sequenceRefetch()
        },
        [sequenceRefetch, updateQuestionTutorial]
    )

    const handleDelete = useCallback(
        async (id: string) => {
            if (sequence !== undefined) {
                const itemIndex = sequence?.findIndex(item => item.id === id)
                setSequence([
                    ...sequence.slice(0, itemIndex),
                    { ...sequence?.[itemIndex], deletionInProgress: true },
                    ...sequence.slice(itemIndex + 1)
                ])
                await deleteQuestionTutorial({
                    variables: {
                        deleteQuestionTutorialId: id
                    }
                })
                await sequenceRefetch()
            }
        },
        [deleteQuestionTutorial, sequence, sequenceRefetch]
    )

    useEffect(() => {
        setSequence(structuredSequence)
    }, [structuredSequence])

    const renderBody = () => {
        if (
            sequence === undefined ||
            sequence === null ||
            sequence.length === 0
        ) {
            return <TutorialContentEmpty />
        }
        return (
            <TutorialContentSequence
                sequence={sequence}
                onMoveItem={handleMoveItem}
                onEndMove={handleEndMove}
                onDelete={handleDelete}
                onEdit={setQuestionToEdit}
            />
        )
    }

    return (
        <>
            <div className="flex">
                <ExclusiveChoiceDropdown<ContentOptionDropdown>
                    testId="select-tutorial-content-options"
                    variant="light"
                    value="Add content"
                    choices={contentOptions}
                    className="w-46 text-center z-20"
                    inputClassName="bg-grape-300 text-white"
                    onClick={setShowModal}
                />
            </div>
            <div className="flex-grow flex-shrink flex-basis-0 overflow-auto -m-6 mt-4 px-6">
                {renderBody()}
            </div>
            {(showModal === ContentOption.explanation.valueOf() ||
                questionToEdit?.type ===
                    turbine.TrainingQuestionTutorialType.FreeMode) && (
                <TutorialExplanationModal
                    question={questionToEdit}
                    onClose={handleModalClose}
                    onAddQuestion={handleTutorialQuestionCreation}
                    isAdding={
                        creatingQuestionTutorial || updatingQuestionTutorial
                    }
                    onEditQuestion={handleEditQuestion}
                />
            )}
            {(showModal === ContentOption.example.valueOf() ||
                questionToEdit?.type ===
                    turbine.TrainingQuestionTutorialType.FromJob) && (
                <TutorialExampleModal
                    orgUsername={orgUsername}
                    question={questionToEdit}
                    onEditQuestion={handleEditQuestion}
                    appID={appID}
                    qualifierID={qualifierID}
                    onClose={handleModalClose}
                    filterAndSort={filterAndSort}
                    onAddQuestion={handleTutorialQuestionCreation}
                    isAdding={
                        creatingQuestionTutorial || updatingQuestionTutorial
                    }
                    params={params}
                />
            )}
        </>
    )
}
